/** IndexedDB スキーマ（仕様書 §6）。 */

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type {
  Category,
  DailySummary,
  Entry,
  MonthKey,
  StrokeDoc,
  TaskEntry,
  Tombstone,
  WeeklyReview,
} from '../domain/types';

export const DB_NAME = 'necosuke-journal';
export const DB_VERSION = 3;

/** IndexedDB上のタスク入力値。key は `d:YYYY-MM-DD` / `w:YYYY-Www`。 */
export interface TaskEntryRecord extends TaskEntry {
  key: string;
  /** daily のみ。月別の取得に使う。 */
  monthKey: MonthKey | '';
  _dirty: boolean;
  _etag: string | null;
}

/** IndexedDB上の一日のまとめ。 */
export interface DailyRecord extends DailySummary {
  monthKey: MonthKey;
  /** NFKC正規化した3項目の結合。検索に使う。 */
  searchText: string;
  _dirty: boolean;
  _etag: string | null;
}

/** IndexedDB上の記事。クラウド上のJSONに、検索・同期用のフィールドを足したもの。 */
export interface EntryRecord extends Entry {
  /** "2026-08"。date から導出。 */
  monthKey: MonthKey;
  /** "2026-08-26"。date から導出。 */
  dateKey: string;
  /** NFKC正規化済みの title + body（§13.2）。 */
  searchText: string;
  /** ローカルで変更され、まだOneDriveへ反映されていない。 */
  _dirty: boolean;
  /** 最後に取得したOneDrive上のeTag。競合検知（If-Match）に使う。 */
  _etag: string | null;
}

export interface MediaRecord {
  id: string;
  entryId: string;
  month: MonthKey;
  file: string;
  mime: string;
  blob: Blob;
  bytes: number;
  lastAccessAt: number;
  /**
   * 取り込み時に読み取った EXIF の位置情報。
   * 縮小の過程で EXIF は失われるため、ここに退避しておく（§11.1）。
   * クラウド側のJSONには載せない端末ローカルの情報。
   */
  exifGps?: { lat: number; lng: number } | null;
  /** OneDriveへのアップロードが完了しているか。false の間はLRU削除の対象外。 */
  _uploaded: boolean;
}

export interface StrokeRecord {
  id: string;
  entryId: string;
  month: MonthKey;
  data: StrokeDoc;
  _uploaded: boolean;
}

export type OutboxOpType =
  | 'entry.upsert'
  | 'entry.delete'
  | 'media.put'
  | 'media.delete'
  | 'categories.put'
  | 'settings.put'
  | 'goals.put'
  | 'daily.upsert'
  | 'daily.delete'
  | 'review.put'
  | 'projects.put'
  | 'tasks.put'
  | 'taskentry.upsert'
  | 'taskentry.delete';

/** 未同期の書き込み操作（§7.2）。 */
export interface OutboxOp {
  opId?: number;
  type: OutboxOpType;
  /** 'entry:<id>' / 'media:<id>' / 'categories' / 'settings' / 'goals' / 'daily:<date>' / 'review:<week>'。同一キーはコアレッシングする。 */
  targetKey: string;
  /** daily / review / タスク入力の対象キー。 */
  dateKey?: string;
  week?: string;
  taskKind?: 'daily' | 'weekly';
  period?: string;
  entryId?: string;
  month?: MonthKey;
  /** 月移動が発生した場合の旧月（§7.6）。 */
  prevMonth?: MonthKey;
  createdAt: string;
  attempts: number;
  nextAttemptAt: string;
  lastError?: string;
  status: 'pending' | 'inflight' | 'failed';
}

export interface MetaRecord {
  key: string;
  value: unknown;
}

interface JournalDB extends DBSchema {
  entries: {
    key: string;
    value: EntryRecord;
    indexes: {
      by_date: string;
      by_month: string;
      by_updatedAt: string;
      by_category: string;
    };
  };
  media: {
    key: string;
    value: MediaRecord;
    indexes: { by_entry: string; by_lastAccess: number };
  };
  strokes: { key: string; value: StrokeRecord; indexes: { by_entry: string } };
  dailies: { key: string; value: DailyRecord; indexes: { by_month: string } };
  reviews: { key: string; value: WeeklyReview };
  taskEntries: {
    key: string;
    value: TaskEntryRecord;
    indexes: { by_kind: string; by_month: string; by_period: string };
  };
  categories: { key: string; value: Category };
  tombstones: { key: string; value: Tombstone; indexes: { by_deletedAt: string } };
  outbox: {
    key: number;
    value: OutboxOp;
    indexes: { by_status: string; by_target: string };
  };
  meta: { key: string; value: MetaRecord };
}

let dbPromise: Promise<IDBPDatabase<JournalDB>> | null = null;

export function getDb(): Promise<IDBPDatabase<JournalDB>> {
  if (!dbPromise) {
    dbPromise = openDB<JournalDB>(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion) {
        // 既存データには触らず、足りないストアだけを作る
        const addTaskEntries = () => {
          const s = db.createObjectStore('taskEntries', { keyPath: 'key' });
          s.createIndex('by_kind', 'kind');
          s.createIndex('by_month', 'monthKey');
          s.createIndex('by_period', 'period');
        };

        if (oldVersion >= 2) {
          addTaskEntries();
          return;
        }
        if (oldVersion === 1) {
          const dailies = db.createObjectStore('dailies', { keyPath: 'date' });
          dailies.createIndex('by_month', 'monthKey');
          db.createObjectStore('reviews', { keyPath: 'week' });
          addTaskEntries();
          return;
        }

        const entries = db.createObjectStore('entries', { keyPath: 'id' });
        entries.createIndex('by_date', 'date');
        entries.createIndex('by_month', 'monthKey');
        entries.createIndex('by_updatedAt', 'updatedAt');
        entries.createIndex('by_category', 'categoryId');

        const media = db.createObjectStore('media', { keyPath: 'id' });
        media.createIndex('by_entry', 'entryId');
        media.createIndex('by_lastAccess', 'lastAccessAt');

        const strokes = db.createObjectStore('strokes', { keyPath: 'id' });
        strokes.createIndex('by_entry', 'entryId');

        db.createObjectStore('categories', { keyPath: 'id' });

        const tombstones = db.createObjectStore('tombstones', { keyPath: 'entryId' });
        tombstones.createIndex('by_deletedAt', 'deletedAt');

        const outbox = db.createObjectStore('outbox', {
          keyPath: 'opId',
          autoIncrement: true,
        });
        outbox.createIndex('by_status', 'status');
        outbox.createIndex('by_target', 'targetKey');

        const dailies = db.createObjectStore('dailies', { keyPath: 'date' });
        dailies.createIndex('by_month', 'monthKey');

        db.createObjectStore('reviews', { keyPath: 'week' });
        addTaskEntries();

        db.createObjectStore('meta', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const db = await getDb();
  const rec = await db.get('meta', key);
  return rec ? (rec.value as T) : fallback;
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key, value });
}
